import { equivalentSeoHtml } from './seoHtmlEquivalent.mjs';

export const SEO_FIELD_LABELS = {
  metaKeyword: 'Meta keywords', pageTitle: 'Page title', metaTitle: 'Meta title', metaDesc: 'Meta description',
  'section:Indication & Dosage': 'Indication & Dosage', 'section:Side Effects': 'Side Effects',
  'section:Prescribing Information': 'Prescribing Information', 'section:Additional Information': 'Additional Information',
  'section:FAQ': 'FAQ', 'section:Suggestions': 'Suggestions',
};
export const SEO_DEFAULT_FIELDS = Object.keys(SEO_FIELD_LABELS);
export const SEO_LANGUAGES = [
  { code: 'en', label: 'English', country: 'US' }, { code: 'cn', label: 'Chinese', country: 'CN' },
  { code: 'ru', label: 'Russian', country: 'RU' }, { code: 'es', label: 'Spanish', country: 'ES' },
  { code: 'sa', label: 'Arabic', country: 'SA' }, { code: 'pt', label: 'Portuguese', country: 'BR' },
];
export const SEO_DEFAULT_REGIONS = Object.fromEntries(SEO_LANGUAGES.map(language => [language.code, language.country]));
export const seoSnapshot = ({ metaKeyword, pageTitle = '', metaTitle = '', metaDesc = '', additionalSectionList = [], additionalSections } = {}) => ({
  ...(metaKeyword !== undefined ? { metaKeyword } : {}), pageTitle, metaTitle, metaDesc,
  additionalSections: (additionalSections || additionalSectionList).filter(({ sectionName }) => Object.prototype.hasOwnProperty.call(SEO_FIELD_LABELS, `section:${sectionName}`)).map(({ sectionName, content }) => ({ sectionName, content: content || '' })),
});
export function seoFieldValue(snapshot, field) {
  return field.startsWith('section:') ? snapshot?.additionalSections?.find(section => section.sectionName === field.slice(8))?.content || '' : snapshot?.[field] || '';
}
const equivalent = (a, b, html = false) => html ? equivalentSeoHtml(a,b) : String(a || '').trim() === String(b || '').trim();

// A SEO draft is an explicit form edit. Never publish or overwrite fields that
// changed while the background job was running; unrelated form state is absent.
export function applySeoMedicineDraft({ fields, selected, baseline, current, setters, allowNewSections = false }) {
  const source = seoSnapshot(current), applied = [], skipped = [];
  const contentChanged = Object.keys(SEO_FIELD_LABELS).filter(key => key.startsWith('section:'))
    .some(key => !equivalent(seoFieldValue(source, key), seoFieldValue(baseline, key), true));
  let sections = [...source.additionalSections], changedSections = false;
  for (const field of selected || []) {
    if (!Object.prototype.hasOwnProperty.call(SEO_FIELD_LABELS, field) || typeof fields?.[field] !== 'string' || !fields[field].trim()) continue;
    const existing = seoFieldValue(source, field), expected = seoFieldValue(baseline, field);
    if (!baseline || !equivalent(existing, expected, field.startsWith('section:')) || (!field.startsWith('section:') && contentChanged)) { skipped.push(field); continue; }
    if (field.startsWith('section:')) {
      const name = field.slice(8), index = sections.findIndex(section => section.sectionName === name);
      // New prose is accepted only from the source-backed generation workflow.
      if ((index < 0 || !existing.trim()) && !allowNewSections) { skipped.push(field); continue; }
      if (index < 0) sections.push({ sectionName: name, content: fields[field] });
      else sections[index] = { ...sections[index], content: fields[field] };
      changedSections = true;
    } else if (setters[field]) setters[field](fields[field]); else { skipped.push(field); continue; }
    applied.push(field);
  }
  if (changedSections) {
    // Preserve additional editor metadata, ordering, custom sections and files.
    const original = current.additionalSectionList || current.additionalSections || [];
    const names = new Set(original.map(section => section.sectionName));
    setters.additionalSectionList?.([
      ...original.map(section => ({ ...section, content: sections.find(item => item.sectionName === section.sectionName)?.content ?? section.content })),
      ...sections.filter(section => !names.has(section.sectionName)),
    ]);
  }
  return { applied, skipped };
}
